<template>
  <!-- Bottone di installazione (opzionale) -->
  <Button v-if="showButton && !$pwa.isPWAInstalled" icon="fas fa-download" size="small" rounded @click="dialogInstallPwa = true" />

  <!-- Dialog installazione PWA -->
  <Dialog
    v-model:visible="dialogInstallPwa"
    :pt="{
      header: { class: 'hidden' },
      mask: {
        style: 'backdrop-filter: blur(10px); background-color: rgba(0, 0, 0, 0.25) !important',
      },
    }"
    :class="[
      'w-full h-[95dvh] max-h-dvh lg:h-auto',
      isMobileD ? 'p-dialog-maximized m-0' : undefined,
      'rounded-none lg:rounded-3xl md:rounded-3xl shadow-2xl backdrop-blur-2xl bg-white/90 dark:bg-surface-900/90 border border-white/20',
      'lg:fixed lg:bottom-8 lg:right-8 lg:w-120 lg:rounded-3xl lg:shadow-2xl',
    ]"
    :position="isMobileD ? 'bottom' : undefined"
    :dismissableMask="!isMobileD"
    contentClass="p-10 text-center overflow-y-auto"
    modal
    @hide="onDialogHide">
    <div class="flex flex-col items-center space-y-5 h-full justify-center">
      <!-- Header con logo e testo -->
      <div class="flex flex-row items-center justify-center w-full gap-4">
        <div class="relative group shrink-0">
          <div
            class="absolute inset-0 blur-3xl bg-linear-to-r from-purple-500/50 via-pink-500/50 to-blue-500/50 rounded-full opacity-70 group-hover:opacity-100 transition duration-500 animate-pulse" />
          <img src="/logo/logoRCM.png" alt="logo" class="h-28 w-28 object-contain relative z-10 dark:hidden drop-shadow-xl" />
          <img src="/logo/logoRCMdark.png" alt="logo" class="h-28 w-28 object-contain relative z-10 hidden dark:block drop-shadow-xl" />
        </div>
        <div class="text-gray-800 dark:text-gray-100 text-left flex-1 border-l border-gray-300 dark:border-gray-700 pl-6">
          <p class="text-lg opacity-85 leading-relaxed">
            <b>{{ t("install_pwa.subtitle") }}.</b><br />
            <span class="text-primary-500 font-medium dark:text-primary-400">
              {{ t("install_pwa.follow_guide") }}
            </span>
          </p>
        </div>
      </div>

      <!-- Sezioni guida -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 w-full text-gray-700 dark:text-gray-200 text-left">
        <div v-for="(section, index) in guideSections" :key="index" :class="section.class">
          <h3 class="text-lg font-semibold mb-3 flex items-center gap-2">
            <i :class="section.icon" />
            <span>{{ t(section.title) }}</span>
          </h3>
          <ul class="list-disc list-inside text-sm leading-relaxed">
            <li v-for="(step, i) in section.steps" :key="i" v-html="t(step)" />
          </ul>
        </div>
      </div>

      <!-- Bottoni azione (solo mobile) -->
      <div v-if="isMobileD" class="flex flex-row gap-4 mt-8">
        <Button :label="t('install_pwa.close')" icon="fas fa-times" class="btn-graphite" size="small" @click.stop="closeDialog" />
        <Button
          v-if="!dontShowPwaInstall"
          :label="t('install_pwa.close_and_dont_show')"
          icon="fas fa-ban"
          class="btn-terracotta"
          size="small"
          @click.stop="dontShowPwaAgain" />
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { useI18n } from "vue-i18n";

// ── Props ────────────────────────────────────────────────────────────────────
const props = defineProps({
  showButton: {
    type: Boolean,
    default: false,
  },
});

// ── Composables & plugin ─────────────────────────────────────────────────────
const { t } = useI18n();
const { $pwa, $localStorage } = useNuxtApp();
const route = useRoute();

// ── Stato globale (condiviso tra istanze) ────────────────────────────────────
// const dialogInstallPwa = useState("dialogInstallPwa", () => false);
const dialogInstallPwa = ref(false);
const hasShownPwaDialog = ref(false); // useState("hasShownPwaDialog", () => false);
const userManuallyClosedDialog = ref(false); //useState("userManuallyClosedPwaDialog", () => false);

// ── Stato locale ─────────────────────────────────────────────────────────────
import { useWindowSize } from "@vueuse/core";
const { width } = useWindowSize();
const isMobileD = computed(() => width.value < 768);

// Reattivo: letto una volta ma usato come ref per coerenza
const dontShowPwaInstall = ref(!!$localStorage.getItem("dontShowPwaInstall"));

// ── Dati statici ─────────────────────────────────────────────────────────────
const guideSections = [
  {
    title: "install_pwa.login_auto.title",
    icon: "fas fa-key text-yellow-500",
    class:
      "p-2 px-6 rounded-2xl bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border border-yellow-400/30 shadow-lg hover:shadow-yellow-400/20 transition-all duration-300 hover:-translate-y-1 md:col-span-3",
    steps: ["install_pwa.login_auto.step1", "install_pwa.login_auto.step2", "install_pwa.login_auto.step3"],
  },
  {
    title: "install_pwa.desktop.title",
    icon: "fas fa-desktop text-blue-500",
    class:
      "p-2 px-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-400/30 shadow-lg hover:shadow-blue-400/20 transition-all duration-300 hover:-translate-y-1 md:col-span-3",
    steps: ["install_pwa.desktop.step1", "install_pwa.desktop.step2", "install_pwa.desktop.step3"],
  },
  {
    title: "install_pwa.android.title",
    icon: "fa-brands fa-android text-green-500",
    class:
      "p-2 px-6 rounded-2xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-400/30 shadow-lg hover:shadow-green-400/20 transition-all duration-300 hover:-translate-y-1 md:col-span-3",
    steps: ["install_pwa.android.step1", "install_pwa.android.step2", "install_pwa.android.step3", "install_pwa.android.step4"],
  },
  {
    title: "install_pwa.ios.title",
    icon: "fa-brands fa-apple text-purple-500",
    class:
      "p-2 px-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-400/30 shadow-lg hover:shadow-purple-400/20 transition-all duration-300 hover:-translate-y-1 md:col-span-3",
    steps: ["install_pwa.ios.step1", "install_pwa.ios.step2", "install_pwa.ios.step3", "install_pwa.ios.step4"],
  },
];

// ── Handlers ─────────────────────────────────────────────────────────────────

/**
 * Chiusura manuale dal bottone.
 * Il Dialog emetterà @hide automaticamente dopo, che gestirà il resto.
 */
const closeDialog = () => {
  userManuallyClosedDialog.value = true;
  dialogInstallPwa.value = false;
};

/**
 * Evento @hide del Dialog — gestisce qualsiasi tipo di chiusura
 * (ESC, click fuori, bottone, ecc.)
 */
const onDialogHide = () => {
  userManuallyClosedDialog.value = true;
};

/**
 * Chiude il dialog e salva la preferenza "non mostrare più"
 */
const dontShowPwaAgain = async () => {
  dontShowPwaInstall.value = true;
  dialogInstallPwa.value = false;
  $localStorage.setItem("dontShowPwaInstall", true);
  await setValuesInIndexedDB({ dontShowPwaInstall: true });
};

// ── Auto-apertura dialog (solo per l'istanza senza bottone) ──────────────────
const maybeShowPwaDialog = async () => {
  if (props.showButton) return;

  // Guard sincrono PRIMA di qualsiasi await
  if (hasShownPwaDialog.value) return;
  if ($pwa.isPWAInstalled) return;
  if (!isMobileD.value) return;
  if (dontShowPwaInstall.value) return;
  if (route.path === "/kiosk") return;
  if (userManuallyClosedDialog.value) return;

  // Segna subito come "già processato" prima degli await
  hasShownPwaDialog.value = true;

  await nextTick();

  // Solo ora fai la chiamata async
  const db = await getValuesFromIndexedDB(["dontShowPwaInstall"]);
  if (db.dontShowPwaInstall) return;

  dialogInstallPwa.value = true;
};

// ── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  await maybeShowPwaDialog();
});
</script>
